import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LodestarLogo } from '@/components/brand';
import { countriesQuery } from '@/data';
import { buildWikiUrl } from '@/features/wiki/architecture';
import { PUBLISHED_COUNTRY_CODES } from '@/features/wiki/published-countries';
import { checklistPath } from '@/features/next-steps/slugs';
import {
  MAP_COUNTRIES,
  MAP_COUNTRY_CONFIG,
  mapPathFor,
} from '@/features/map-core/countries';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { uiLang } from '@/i18n/lang';
import { submitNotify } from '@/lib/notify';

export function SiteFooter() {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n.language);
  const lp = useLocalizedPath();
  const { data: file } = useQuery(countriesQuery());
  const countryNames = useMemo(() => {
    const map: Record<string, string> = {};
    file?.countries.forEach((c) => {
      map[c.code] = lang === 'ru' ? c.name_ru : c.name_en;
    });
    return map;
  }, [file, lang]);
  return (
    <footer className="border-t border-ink-100 bg-paper">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <NewsletterSignup />
        <div className="mt-8 grid gap-8 border-t border-ink-100 pt-8 sm:grid-cols-[2fr_1fr_1fr]">
          <div className="flex flex-col gap-3">
            <LodestarLogo size={28} withTagline />
            <p className="max-w-sm text-xs leading-relaxed text-ink-500">
              {t('home.footer.tagline', {
                defaultValue:
                  'International relocation guidance across 31 countries — quiz, atlas, journalistic country guides.',
              })}
            </p>
          </div>

          <div>
            <p className="mb-3 font-mono text-[11px] uppercase tracking-wider text-ink-900">
              {t('home.footer.guidesTitle', { defaultValue: 'Country guides' })}
            </p>
            <ul className="space-y-2 text-sm">
              {PUBLISHED_COUNTRY_CODES.map((cc) => (
                <li key={cc} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <Link
                    to={buildWikiUrl({ lang, cc })}
                    className="text-ink-700 hover:text-terra-600"
                  >
                    {countryNames[cc] ?? cc}
                  </Link>
                  <Link
                    to={checklistPath(cc)}
                    className="font-mono text-[10px] uppercase tracking-wider text-ink-500 hover:text-terra-600"
                  >
                    {t('home.footer.checklist', { defaultValue: 'Checklist' })}
                  </Link>
                </li>
              ))}
              <li className="pt-1">
                <Link
                  to={lp('/countries')}
                  className="font-mono text-[11px] uppercase tracking-wider text-ink-500 hover:text-ink-900"
                >
                  {t('home.footer.allCountries', { defaultValue: 'All 31 countries →' })}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="mb-3 font-mono text-[11px] uppercase tracking-wider text-ink-900">
              {t('home.footer.exploreTitle', { defaultValue: 'Explore' })}
            </p>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to={lp('/map')} className="text-ink-700 hover:text-terra-600">
                  {t('home.footer.worldMap', { defaultValue: 'World map' })}
                </Link>
              </li>
              {/* One entry per country that actually has an interactive map —
                  driven by the map registry, not a hand-kept footer list. */}
              {MAP_COUNTRIES.map((cc) => {
                const href = mapPathFor(cc);
                if (!href) return null;
                const cfg = MAP_COUNTRY_CONFIG[cc];
                return (
                  <li key={cc}>
                    <Link to={href} className="text-ink-700 hover:text-terra-600">
                      {t('home.footer.countryMap', {
                        country: lang === 'ru' ? cfg.nameRu : cfg.nameEn,
                        defaultValue: '{{country}} interactive map',
                      })}
                    </Link>
                  </li>
                );
              })}
              <li>
                <Link to="/quiz" className="text-ink-700 hover:text-terra-600">
                  {t('home.footer.quiz', { defaultValue: 'Take the quiz' })}
                </Link>
              </li>
              <li>
                <Link to={lp('/topics')} className="text-ink-700 hover:text-terra-600">
                  {t('home.footer.topics', { defaultValue: 'Browse topics' })}
                </Link>
              </li>
              <li>
                <Link to={lp('/guide')} className="text-ink-700 hover:text-terra-600">
                  {t('home.footer.guideLink', { defaultValue: 'How to use the site' })}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-start gap-3 border-t border-ink-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <ul className="flex flex-wrap gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-wider text-ink-500">
            <li>
              <Link to={lp('/report')} className="hover:text-ink-900">
                {t('home.footer.report', { defaultValue: 'Relocation report' })}
              </Link>
            </li>
            <li>
              <Link to="/partners" className="hover:text-ink-900">
                {t('home.footer.partners', { defaultValue: 'For consultants' })}
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="hover:text-ink-900">
                {t('home.footer.privacy')}
              </Link>
            </li>
            <li>
              <Link to="/terms" className="hover:text-ink-900">
                {t('home.footer.terms')}
              </Link>
            </li>
            <li>
              <Link to={lp('/refund')} className="hover:text-ink-900">
                {t('home.footer.refund', { defaultValue: 'Refund policy' })}
              </Link>
            </li>
          </ul>
          <p className="font-mono text-[11px] tracking-wider text-ink-500">
            {t('home.footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
}

function NewsletterSignup() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes('@') || submitting) return;
    setSubmitting(true);
    setFailed(false);
    // The confirmation below promises an email. Only show it when the API
    // actually took the address — otherwise keep the form, keep what was
    // typed, and say plainly that nothing was saved.
    //
    // A 2xx here means "confirmation email sent", not "subscribed": this is
    // the one surface on double opt-in, and the address is enrolled only
    // after the visitor clicks the button in that email. The success copy
    // below says so — promising the digest outright would be the same kind
    // of lie the honest-result handling above was written to end.
    const ok = await submitNotify({
      email,
      countryCode: 'NEWSLETTER',
      source: 'newsletter',
      context: { surface: 'site-footer' },
    });
    setSubmitting(false);
    if (ok) setDone(true);
    else setFailed(true);
  };

  if (done) {
    return (
      <p
        className="font-mono text-[11px] text-[color:var(--color-moss-600)]"
        data-testid="newsletter-done"
      >
        {/* No defaultValue: the key exists in en.json and ru.json, and an
            inline default is how English has leaked onto RU pages before. */}
        ✓ {t('home.footer.newsletter.confirm')}
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-center">
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[11px] uppercase tracking-wider text-ink-500">
          {t('home.footer.newsletter.label', { defaultValue: 'Monthly digest' })}
        </p>
        {failed && (
          <p
            className="mt-1 font-mono text-[11px] text-rust-600"
            role="status"
            data-testid="newsletter-error"
          >
            {t('home.footer.newsletter.failed')}
          </p>
        )}
        <p className="mt-1 text-[13px] text-ink-700">
          {t('home.footer.newsletter.copy', {
            defaultValue:
              'One thoughtful email a month — new countries we cover, data updates, relocation reads. No spam, unsubscribe in one click.',
          })}
        </p>
      </div>
      <div className="flex w-full shrink-0 gap-2 sm:w-auto">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('home.footer.newsletter.placeholder', { defaultValue: 'you@example.com' })}
          required
          aria-label={t('home.footer.newsletter.placeholder', { defaultValue: 'you@example.com' })}
          className="min-w-0 flex-1 rounded border border-ink-200 bg-paper px-3 py-2 font-mono text-xs text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-1 focus:ring-terra-600 sm:w-56"
          data-testid="newsletter-email"
        />
        <button
          type="submit"
          disabled={submitting || !email.includes('@')}
          className="shrink-0 rounded bg-terra-600 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-wider text-white hover:bg-terra-700 disabled:opacity-50"
          data-testid="newsletter-submit"
        >
          {submitting
            ? t('home.footer.newsletter.sending', { defaultValue: 'Sending…' })
            : t('home.footer.newsletter.submit', { defaultValue: 'Subscribe' })}
        </button>
      </div>
    </form>
  );
}
